import React, { useState, useEffect } from 'react';
import API from '../API';

const DashboardSimple = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching organizations...');
      const response = await API.getOrganizations();
      console.log('Response received:', response);
      
      const data = response.data || [];
      console.log('Organizations data:', data);
      
      setOrganizations(data);
      setLoading(false);
      
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Failed to load data');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">
            <span role="img" aria-label="warning">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchData}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Calculate basic stats
  const totalTenants = organizations.length;
  const totalUsers = organizations.reduce((sum, org) => sum + (org.maxUsers || 0), 0);
  const totalPrinters = organizations.reduce((sum, org) => sum + (org.maxPrinterModels || 0), 0);
  const totalLabels = organizations.reduce((sum, org) => sum + (org.maxLabels || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-gray-600">
            Overview of tenant usage and activity
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Total Tenants
            </h3>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {totalTenants}
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Total Users
            </h3>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {totalUsers}
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Printer Models
            </h3>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {totalPrinters}
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Total Labels
            </h3>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {totalLabels}
            </p>
          </div>
        </div>

        {/* Organizations List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Organizations ({totalTenants})
          </h2>
          
          <div className="space-y-4">
            {organizations.length > 0 ? (
              organizations.map((org, index) => (
                <div key={org.id || index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {org.name || 'Unnamed Organization'}
                      </h4>
                      <div className="mt-2 grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Users:</span> {org.maxUsers || 0}
                        </div>
                        <div>
                          <span className="font-medium">Printers:</span> {org.maxPrinterModels || 0}
                        </div>
                        <div>
                          <span className="font-medium">Labels:</span> {org.maxLabels || 0}
                        </div>
                        <div>
                          <span className="font-medium">Subscription:</span> {org.subscriptionType || 'Free'}
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        (org.maxUsers || 0) > 5 
                          ? 'bg-green-100 text-green-800' 
                          : (org.maxUsers || 0) > 0 
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        {(org.maxUsers || 0) > 5 ? 'High Usage' : 
                         (org.maxUsers || 0) > 0 ? 'Medium Usage' : 'Low Usage'}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No organizations found
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSimple;
